'use client';

import { useMemoizedFn, useSetState } from 'ahooks';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useState } from 'react';
import { getCreationDetail } from '@/app/apis';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_IMAGE_FILE_SIZE, SITE } from '@/app/constants';
import { useDevice, useFetch, useGlobalModal, useRunTempUploadBatch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { CreationInfo, CreationWithTask, FormImagePrompt, ImagePrompt, ImageUploadFile } from '@/app/types';
import { mergeCreationAndTask } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { UpscaleResolutionSelector } from '../_components/upscale/UpscaleResolutionSelector';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  createImagePromptFromCreation,
  createPromptListHelpers,
  createToolImagePromptFromUploadFile,
  formatUpscaleResolutionLabel,
  getDefaultUpscaleTargetResolution,
  getUpscaleSourceResolution,
  isResolutionBelow4k,
  isUpscaleTargetResolutionAvailable,
  UPSCALE_SOURCE_RESOLUTION_ALIASES,
  type UpscaleSourceResolution,
} from '../_utils';
import { getToolTempImagesPendingItems } from '../_utils/tool-temp-upload-items';

interface UpscaleState {
  selectedCreation?: CreationWithTask;
}

const UpscaleForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const pathname = usePathname();
  const router = useRouter();
  const storeApi = useStoreApi();
  const searchParams = useSearchParams();
  const creationId = searchParams.get('creationId') || '';

  const { settings, updateSettings } = useToolForm('upscale_image');

  const [openMediaSelector, setOpenMediaSelector] = useState(false);
  const [selectedCreationIndex, setSelectedCreationIndex] = useState<number | null>(null);
  const [upscaleState, setUpscaleState] = useSetState<UpscaleState>({
    selectedCreation: undefined,
  });

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateUpscaleImageFormParams, imagePrompts, userInfo, selectedCreationId } = useStore((state) => ({
    updateUpscaleImageFormParams: state.updateUpscaleImageFormParams,
    imagePrompts: state.upscaleImageFormParams.imagePrompts,
    userInfo: state.userInfo,
    selectedCreationId: state.upscaleImageFormParams.creationId,
  }));

  const imagePrompt = imagePrompts?.[0] || null;

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().upscaleImageFormParams.imagePrompts,
          (prompts) => updateUpscaleImageFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateUpscaleImageFormParams, userInfo],
  );

  const showInvalidResolutionToast = useMemoizedFn(() => {
    toast({
      type: 'error',
      message: t('create.createForm.upload.error.invalidResolution4k'),
    });
  });

  const isCreationResolutionAtLeast4k = useCallback((creation: CreationInfo | CreationWithTask): boolean => {
    return !isResolutionBelow4k({
      width: creation.resolution?.width,
      height: creation.resolution?.height,
    });
  }, []);

  const getCreationSourceResolution = useCallback(
    (creation: CreationInfo | CreationWithTask): UpscaleSourceResolution | null => {
      return getUpscaleSourceResolution(
        {
          resolution: creation.resolution?.resolution,
          width: creation.resolution?.width,
          height: creation.resolution?.height,
        },
        UPSCALE_SOURCE_RESOLUTION_ALIASES,
      );
    },
    [],
  );

  const syncResolutionSelection = useCallback(
    (sourceResolution?: UpscaleSourceResolution | null, currentSelectedResolution?: string) => {
      const nextResolution = isUpscaleTargetResolutionAvailable(currentSelectedResolution, sourceResolution)
        ? currentSelectedResolution
        : getDefaultUpscaleTargetResolution(sourceResolution);

      if (settings.resolution === nextResolution) {
        return;
      }

      updateSettings({
        resolution: nextResolution,
      });
    },
    [settings.resolution, updateSettings],
  );

  const applyCreationSelection = useCallback(
    (creation: CreationInfo | CreationWithTask) => {
      if (isCreationResolutionAtLeast4k(creation)) {
        showInvalidResolutionToast();
        return;
      }

      applyToolFormParamsPatch(updateUpscaleImageFormParams, {
        imagePrompts: [createImagePromptFromCreation(creation)],
        creationId: creation.id,
      });
      syncResolutionSelection(getCreationSourceResolution(creation), settings.resolution);
    },
    [
      getCreationSourceResolution,
      isCreationResolutionAtLeast4k,
      settings.resolution,
      showInvalidResolutionToast,
      syncResolutionSelection,
      updateUpscaleImageFormParams,
    ],
  );

  // 从 imagePrompt 中提取图片信息
  const currentResolution = getUpscaleSourceResolution(
    {
      resolution: (imagePrompt?.prompt as ImagePrompt & { resolution?: { resolution?: string } })?.resolution
        ?.resolution,
      width: (imagePrompt?.prompt as ImagePrompt & { resolution?: { width?: number } })?.resolution?.width,
      height: (imagePrompt?.prompt as ImagePrompt & { resolution?: { height?: number } })?.resolution?.height,
    },
    UPSCALE_SOURCE_RESOLUTION_ALIASES,
  );
  const currentResolutionLabel = formatUpscaleResolutionLabel(currentResolution);
  const selectedResolution =
    (settings as { resolution?: string }).resolution || getDefaultUpscaleTargetResolution(currentResolution);

  useFetch(
    () =>
      getCreationDetail({
        path: {
          id: creationId,
        },
      }),
    {
      ready: !!creationId && imagePrompt?.id !== creationId,
      refreshDeps: [creationId, imagePrompt?.id],
      onSuccess: ({ data, error }) => {
        if (error || !data?.creation) return;

        const mergedCreation = mergeCreationAndTask({
          creation: data.creation,
          task: data.task,
          creationId: data.creation.id,
          extendSourceCreations: data.extend_source_creations,
        });

        if (mergedCreation.type !== 'image') {
          return;
        }

        applyCreationSelection(mergedCreation);
      },
    },
  );

  useEffect(() => {
    if (!currentResolution) {
      return;
    }

    if (isUpscaleTargetResolutionAvailable(settings.resolution, currentResolution)) {
      return;
    }

    syncResolutionSelection(currentResolution, settings.resolution);
  }, [currentResolution, settings.resolution, syncResolutionSelection]);

  useEffect(() => {
    if (!creationId || imagePrompt?.id !== creationId) {
      return;
    }

    router.replace(pathname);
  }, [creationId, imagePrompt?.id, pathname, router]);

  const handleSelectMedia = useCallback(
    (index: number | null, creation?: CreationWithTask) => {
      if (!creation) {
        setSelectedCreationIndex(null);
        setUpscaleState({
          selectedCreation: undefined,
        });
        return;
      }
      setSelectedCreationIndex(index);
      setUpscaleState({
        selectedCreation: creation,
      });
    },
    [setUpscaleState],
  );

  const handleRemoveMedia = useCallback(() => {
    updateUpscaleImageFormParams('imagePrompts', []);
    updateUpscaleImageFormParams('creationId', undefined);
  }, [updateUpscaleImageFormParams]);

  const handleMediaUpload = useMemoizedFn((file: ImageUploadFile) => {
    const sourceResolution = getUpscaleSourceResolution(
      {
        width: file.file?.sourceImageWidth || file.width,
        height: file.file?.sourceImageHeight || file.height,
      },
      UPSCALE_SOURCE_RESOLUTION_ALIASES,
    );
    const imagePrompt = createToolImagePromptFromUploadFile(
      file,
      file.file
        ? ({
            ...file.file,
            resolution: {
              width: file.width || 0,
              height: file.height || 0,
              resolution: sourceResolution || '',
            },
          } as ImagePrompt & {
            resolution: {
              width: number;
              height: number;
              resolution: string;
            };
          })
        : null,
    );
    updateUpscaleImageFormParams('imagePrompts', [imagePrompt]);

    if (file.status === 'done' && file.file) {
      syncResolutionSelection(sourceResolution, settings.resolution);
    }

    if (file.status === 'error' || file.status === 'removed') {
      updateUpscaleImageFormParams('imagePrompts', []);
    }
  });

  const getSelectedImage = useCallback(
    async (url: string) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
        if (!isResolutionBelow4k({ width: img.width, height: img.height })) {
          showInvalidResolutionToast();
          URL.revokeObjectURL(img.src);
          return null;
        }

        const imageData = { blob, width: img.width, height: img.height };
        URL.revokeObjectURL(img.src);
        return imageData;
      } catch {
        return null;
      }
    },
    [showInvalidResolutionToast],
  );

  const validateImageResolution = useCallback(
    (imageResolution: { width: number; height: number }) => {
      if (!isResolutionBelow4k(imageResolution)) {
        return {
          errorMessage: t('create.createForm.upload.error.invalidResolution4k'),
          errorReason: 'invalidResolution4k',
        };
      }

      return null;
    },
    [t],
  );

  const hasUploadingMedia = imagePrompts.some((prompt) => prompt.status === 'uploading');
  const remainingImageSlots = imagePrompt ? 0 : 1;

  return (
    <>
      <ToolFormContainer
        title={t('tools.upscale.image.title')}
        description={t('tools.upscale.image.description')}
        exampleSection={<ExampleMediaPreview tool="upscale_image" />}
        mediaSection={
          <MediaDropZone
            maxImageCount={remainingImageSlots}
            disabled={remainingImageSlots === 0}
            getSelectedImage={getSelectedImage}
            validateImageResolution={validateImageResolution}
            allowTempMode={true}
            enablePaste={true}
            onChange={(file, type) => {
              if (type === 'image') handleMediaUpload(file as ImageUploadFile);
            }}
          >
            <ToolMediaUpload
              type="image"
              header={
                <ToolMediaUploadHeader
                  icon={<ProductLinePic className="size-5 text-system-white" />}
                  title={t('tools.targetImage')}
                  rightSlot={
                    currentResolutionLabel ? (
                      <div className="flex items-center gap-1 text-sm text-system-white leading-[1.4]">
                        <span className="text-system-white/40">{t('tools.upscale.video.current')}: </span>
                        <span className="font-medium">{currentResolutionLabel}</span>
                      </div>
                    ) : null
                  }
                />
              }
              emptyActionsLayout="inline"
              imagePrompt={imagePrompt}
              isMobile={isMobile}
              showImageWatermark={
                Boolean(selectedCreationId && imagePrompt?.id === selectedCreationId) &&
                (SITE === 'china' || userInfo?.subs_plan === 'free')
              }
              maxFileSize={MAX_IMAGE_FILE_SIZE}
              validateImageResolution={validateImageResolution}
              getSelectedImage={getSelectedImage}
              onImageUpload={handleMediaUpload}
              onOpenSelector={() => setOpenMediaSelector(true)}
              onRemoveMedia={handleRemoveMedia}
            />
          </MediaDropZone>
        }
        settingsSection={
          <UpscaleResolutionSelector
            selectedResolution={selectedResolution}
            onResolutionChange={(resolution) => {
              updateSettings({ resolution });
            }}
            currentResolution={currentResolution || undefined}
          />
        }
        submitButton={
          <ToolFormSubmitButton
            taskType="upscale_image"
            disabled={hasUploadingMedia || !imagePrompt || imagePrompt.status !== 'done'}
            disabledTip={hasUploadingMedia ? t('create.createForm.error.uploadingMedia') : undefined}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      />

      <CreationSelectorDialog
        open={openMediaSelector}
        onOpenChange={setOpenMediaSelector}
        selectedIndex={selectedCreationIndex}
        mediaType="image"
        onSelectionChange={handleSelectMedia}
        isCreationDisabled={isCreationResolutionAtLeast4k}
        onConfirm={() => {
          setOpenMediaSelector(false);
          const selectedCreation = upscaleState.selectedCreation;
          if (selectedCreation) {
            applyCreationSelection(selectedCreation);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default UpscaleForm;
